import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Coins, 
  TrendingUp, 
  TrendingDown,
  Users,
  Copy,
  ExternalLink,
  Search,
  RefreshCw,
  ArrowUpRight,
  ArrowDownLeft,
  Star,
  BarChart3,
  DollarSign,
  Activity
} from 'lucide-react';
import {
  VTTN_CIRCULATING,
  VTTN_HOLDERS,
  VTTN_PRICE_USD,
  VTTN_TOTAL_SUPPLY,
} from '../constants/vttn';

interface Token {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  circulatingSupply: string;
  price: number;
  priceChange24h: number;
  marketCap: number;
  volume24h: number;
  holders: number;
  transfers24h: number;
  type: 'native' | 'erc20' | 'energy' | 'staking';
  verified: boolean;
  logo?: string;
}

interface TokenHolder {
  address: string;
  balance: string;
  percentage: number;
  rank: number;
}

const TokensPage: React.FC = () => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<Token[]>([]);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [holders, setHolders] = useState<TokenHolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('marketCap');
  const [currentPage, setCurrentPage] = useState(1);
  const tokensPerPage = 20;

  useEffect(() => {
    fetchTokens();
    const interval = setInterval(fetchTokens, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, [filterType, sortBy]);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      
      // 실제 Voltron 토큰 정보 조회
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const VTTN_TOKEN_ADDRESS = '0x0Eb3Dade9b6366E28DF7D789894e297b165725F5'; // 실제 Voltron 토큰 주소
      
      let tokens: Token[] = [];
      
      try {
        // VTTN 토큰 정보 조회
        const [balanceResponse, statsResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/explorer/addresses/${VTTN_TOKEN_ADDRESS}`),
          fetch(`${apiBaseUrl}/explorer/network-stats`)
        ]);
        
        if (balanceResponse.ok && statsResponse.ok) {
          const balanceData = await balanceResponse.json();
          const statsData = await statsResponse.json();
          
          // VTTN 토큰 정보 구성
          const price = statsData.stats?.vttnPrice || VTTN_PRICE_USD;
          const vttnToken: Token = {
            address: VTTN_TOKEN_ADDRESS,
            name: 'Voltron Token',
            symbol: 'VTTN',
            decimals: 18,
            totalSupply: String(VTTN_TOTAL_SUPPLY),
            circulatingSupply: String(VTTN_CIRCULATING),
            price,
            priceChange24h: statsData.stats?.priceChange || 0,
            marketCap: price * VTTN_CIRCULATING,
            volume24h: 0,
            holders: VTTN_HOLDERS,
            transfers24h: 0, // 추후 계산
            type: 'native',
            verified: true
          };
          
          tokens = [vttnToken];
        }
      } catch (error) {
        console.error('토큰 정보 조회 실패:', error);
        // 기본 VTTN 토큰 정보
        tokens = [{
          address: VTTN_TOKEN_ADDRESS,
          name: 'Voltron Token',
          symbol: 'VTTN',
          decimals: 18,
          totalSupply: String(VTTN_TOTAL_SUPPLY),
          circulatingSupply: String(VTTN_CIRCULATING),
          price: VTTN_PRICE_USD,
          priceChange24h: 0,
          marketCap: VTTN_PRICE_USD * VTTN_CIRCULATING,
          volume24h: 0,
          holders: VTTN_HOLDERS,
          transfers24h: 0,
          type: 'native',
          verified: true
        }];
      }
      
      // 필터링 및 정렬
      let filteredTokens = tokens;
      
      if (filterType !== 'all') {
        filteredTokens = filteredTokens.filter(token => token.type === filterType);
      }
      
      if (searchTerm) {
        filteredTokens = filteredTokens.filter(token => 
          token.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          token.address.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // 정렬
      filteredTokens.sort((a, b) => {
        switch (sortBy) {
          case 'marketCap':
            return b.marketCap - a.marketCap;
          case 'volume':
            return b.volume24h - a.volume24h;
          case 'price':
            return b.price - a.price;
          case 'holders':
            return b.holders - a.holders;
          default:
            return b.marketCap - a.marketCap;
        }
      });
      
      setTokens(filteredTokens);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'native': return 'bg-purple-100 text-purple-800';
      case 'erc20': return 'bg-blue-100 text-blue-800';
      case 'energy': return 'bg-green-100 text-green-800';
      case 'staking': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const paginatedTokens = tokens.slice(
    (currentPage - 1) * tokensPerPage,
    currentPage * tokensPerPage
  );

  const totalPages = Math.ceil(tokens.length / tokensPerPage);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Tokens</h1>
          <p className="text-slate-600">Explore all tokens on Voltron Mainnet</p>
        </div>
        <button
          onClick={fetchTokens}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Tokens</p>
              <p className="text-2xl font-bold text-gray-900">{tokens.length}</p>
            </div>
            <Coins className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Market Cap</p>
              <p className="text-2xl font-bold text-green-600">
                {formatNumber(tokens.reduce((sum, token) => sum + token.marketCap, 0))}
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">24h Volume</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatNumber(tokens.reduce((sum, token) => sum + token.volume24h, 0))}
              </p>
            </div>
            <Activity className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Holders</p>
              <p className="text-2xl font-bold text-purple-600">
                {tokens.reduce((sum, token) => sum + token.holders, 0).toLocaleString()}
              </p>
            </div>
            <Users className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, symbol, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="native">Native</option>
            <option value="erc20">ERC-20</option>
            <option value="energy">Energy</option>
            <option value="staking">Staking</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="marketCap">Market Cap</option>
            <option value="volume">Volume</option>
            <option value="price">Price</option>
            <option value="holders">Holders</option>
          </select>
        </div>
      </div>

      {/* 토큰 테이블 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Token</th>
                <th>Price</th>
                <th>24h Change</th>
                <th>Market Cap</th>
                <th>Volume 24h</th>
                <th>Holders</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <div className="flex justify-center items-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                      <span className="text-gray-500">Loading tokens...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedTokens.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-500">
                    No tokens found
                  </td>
                </tr>
              ) : (
                paginatedTokens.map((token, index) => (
                  <tr key={token.address} className="hover:bg-gray-50">
                    <td>
                      <span className="font-medium text-gray-900">
                        {(currentPage - 1) * tokensPerPage + index + 1}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <Coins className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => navigate(`/token/${token.address}`)}
                              className="font-medium text-blue-600 hover:text-blue-800"
                            >
                              {token.name}
                            </button>
                            {token.verified && (
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            )}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-sm text-gray-500 font-mono">{token.symbol}</span>
                            <button
                              onClick={() => copyToClipboard(token.address)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="font-medium text-gray-900">
                        ${token.price.toFixed(6)}
                      </span>
                    </td>
                    <td>
                      <div className={`flex items-center space-x-1 ${
                        token.priceChange24h >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {token.priceChange24h >= 0 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">
                          {token.priceChange24h >= 0 ? '+' : ''}{token.priceChange24h.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm font-medium text-gray-900">
                        ${(token.marketCap / 1000000).toFixed(2)}M
                      </span>
                    </td>
                    <td>
                      <span className="text-sm font-medium text-gray-900">
                        ${(token.volume24h / 1000000).toFixed(2)}M
                      </span>
                    </td>
                    <td>
                      <span className="text-sm font-medium text-gray-900">
                        {token.holders.toLocaleString()}
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${
                        getTypeColor(token.type)
                      }`}>
                        {token.type}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 border rounded-lg ${
                  currentPage === page
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}


    </motion.div>
  );
};

export default TokensPage;
